import { PageLoader } from '@/components/ui/loader';
import { useState } from 'react';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, BookOpenCheck, Plus, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox, Select, Switch, Textarea } from '@/components/ui/misc';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  RECORD_LABELS,
  useDeletePlaybook,
  usePlaybooks,
  useSavePlaybook,
  type Playbook,
  type PlaybookQuestion,
  type RecordType,
} from './api';

const TYPES: [PlaybookQuestion['type'], string][] = [
  ['text', 'Short answer'],
  ['textarea', 'Long answer'],
  ['select', 'One choice'],
  ['multiselect', 'Several choices'],
  ['checkbox', 'Yes / no'],
  ['number', 'Number'],
];

let counter = 0;
const newId = () => `q${Date.now().toString(36).slice(-4)}${(counter += 1)}`;

/** BANT, the qualifying questions most sales teams start with. */
function starter(): PlaybookQuestion[] {
  return [
    { id: newId(), type: 'textarea', label: 'What problem are they trying to solve?', required: true },
    { id: newId(), type: 'select', label: 'Budget', options: ['Under AED 10k', 'AED 10k – 50k', 'AED 50k – 200k', 'Over AED 200k', 'Not decided'], required: false },
    { id: newId(), type: 'checkbox', label: 'Are we speaking with the decision maker?', required: false },
    { id: newId(), type: 'text', label: 'Who else is involved in the decision?', required: false },
    { id: newId(), type: 'select', label: 'Timeline', options: ['This month', 'This quarter', 'This year', 'Just researching'], required: false },
    { id: newId(), type: 'multiselect', label: 'What interests them', options: ['Pricing', 'Demo', 'Integration', 'Support', 'Implementation'], required: false },
    { id: newId(), type: 'textarea', label: 'Agreed next step', required: false },
  ];
}

/** Playbooks (HubSpot): call scripts and qualifying questions, answered on the record. */
export function PlaybooksPage() {
  const { data: playbooks = [], isLoading } = usePlaybooks();
  const [editing, setEditing] = useState<Playbook | 'new' | null>(null);

  return (
    <div>
      <PageHeader
        title="Playbooks"
        description="The questions to ask on a call, in order. Reps open one from the record; the answers are saved to its timeline."
        breadcrumbs={[{ label: 'Sales hub' }, { label: 'Playbooks' }]}
        actions={
          <Can permission="crm.playbook.create">
            <Button onClick={() => setEditing('new')}>
              <Plus /> New playbook
            </Button>
          </Can>
        }
      />
      {isLoading ? (
        <PageLoader />
      ) : playbooks.length === 0 ? (
        <div className="rounded-2xl border border-dashed p-10 text-center">
          <BookOpenCheck className="mx-auto h-10 w-10 text-primary/60" />
          <p className="mt-3 font-semibold">No playbooks yet</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">Start from the qualification call: need, budget, decision maker, timeline.</p>
          <Can permission="crm.playbook.create">
            <Button className="mt-4" onClick={() => setEditing('new')}>
              Create a playbook
            </Button>
          </Can>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {playbooks.map((playbook) => (
            <Card key={playbook.id} className="cursor-pointer hover:shadow-md" onClick={() => setEditing(playbook)}>
              <CardContent className="space-y-2 pt-5">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold">{playbook.name}</p>
                  <Badge className={playbook.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-200 text-neutral-600'}>
                    {playbook.isActive ? 'On' : 'Off'}
                  </Badge>
                </div>
                {playbook.description && <p className="line-clamp-2 text-sm text-muted-foreground">{playbook.description}</p>}
                <p className="text-xs text-muted-foreground">
                  {playbook.questions.length} questions · used {playbook.runCount}× ·{' '}
                  {playbook.entityTypes.map((type) => RECORD_LABELS[type]).join(', ')}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {editing && <PlaybookEditor playbook={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function PlaybookEditor({ playbook, onClose }: { playbook: Playbook | null; onClose: () => void }) {
  const save = useSavePlaybook();
  const remove = useDeletePlaybook();
  const [name, setName] = useState(playbook?.name ?? 'Qualification call');
  const [description, setDescription] = useState(playbook?.description ?? '');
  const [entityTypes, setEntityTypes] = useState<RecordType[]>(playbook?.entityTypes ?? ['lead', 'deal']);
  const [questions, setQuestions] = useState<PlaybookQuestion[]>(playbook?.questions ?? starter());
  const [isActive, setIsActive] = useState(playbook?.isActive ?? true);
  const [error, setError] = useState<string | null>(null);

  const update = (index: number, patch: Partial<PlaybookQuestion>) =>
    setQuestions((current) => current.map((question, at) => (at === index ? { ...question, ...patch } : question)));
  const move = (index: number, by: number) =>
    setQuestions((current) => {
      const next = [...current];
      const [item] = next.splice(index, 1);
      next.splice(index + by, 0, item);
      return next;
    });

  const submit = async () => {
    setError(null);
    try {
      await save.mutateAsync({ id: playbook?.id, name, description: description || null, entityTypes, questions, isActive });
      toast.success('Playbook saved');
      onClose();
    } catch (failure) {
      setError(failure instanceof ApiRequestError ? failure.message : 'Could not save');
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{playbook ? 'Edit playbook' : 'New playbook'}</DialogTitle>
          <DialogDescription>Reps see these questions beside the record while they talk.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={name} onChange={(event) => setName(event.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>When to use it</Label>
            <Textarea rows={2} value={description} onChange={(event) => setDescription(event.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Offered on</Label>
            <div className="flex flex-wrap gap-3">
              {(['lead', 'contact', 'company', 'deal'] as RecordType[]).map((type) => (
                <label key={type} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={entityTypes.includes(type)}
                    onCheckedChange={(value) => setEntityTypes((current) => (value ? [...current, type] : current.filter((item) => item !== type)))}
                  />
                  {RECORD_LABELS[type]}
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            {questions.map((question, index) => (
              <div key={question.id} className="space-y-2 rounded-xl border p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-muted-foreground">{index + 1}</span>
                  <Input className="min-w-[12rem] flex-1" value={question.label} onChange={(event) => update(index, { label: event.target.value })} />
                  <Select className="w-40" value={question.type} onChange={(event) => update(index, { type: event.target.value as PlaybookQuestion['type'] })}>
                    {TYPES.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                  <Button size="sm" variant="ghost" disabled={index === 0} onClick={() => move(index, -1)}>
                    <ArrowUp />
                  </Button>
                  <Button size="sm" variant="ghost" disabled={index === questions.length - 1} onClick={() => move(index, 1)}>
                    <ArrowDown />
                  </Button>
                  <Button size="sm" variant="ghost" disabled={questions.length === 1} onClick={() => setQuestions((current) => current.filter((_, at) => at !== index))}>
                    <Trash2 />
                  </Button>
                </div>
                {(question.type === 'select' || question.type === 'multiselect') && (
                  <Input
                    placeholder="Options, separated by commas"
                    value={(question.options ?? []).join(', ')}
                    onChange={(event) => update(index, { options: event.target.value.split(',').map((option) => option.trim()).filter(Boolean) })}
                  />
                )}
                <div className="flex flex-wrap items-center gap-3">
                  <Input
                    className="min-w-[12rem] flex-1"
                    placeholder="Hint for the rep (optional)"
                    value={question.help ?? ''}
                    onChange={(event) => update(index, { help: event.target.value || null })}
                  />
                  <label className="flex items-center gap-1.5 text-xs">
                    <Checkbox checked={question.required} onCheckedChange={(value) => update(index, { required: Boolean(value) })} /> Required
                  </label>
                </div>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => setQuestions((current) => [...current, { id: newId(), type: 'text', label: '', required: false }])}>
              <Plus /> Add question
            </Button>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={isActive} onCheckedChange={setIsActive} /> Offer it to reps
          </label>
          <FormError message={error} />
        </div>
        <DialogFooter className={cn(playbook && 'sm:justify-between')}>
          {playbook && (
            <Can permission="crm.playbook.delete">
              <Button
                variant="ghost"
                className="text-destructive"
                onClick={() => window.confirm('Delete this playbook? Saved answers stay on the records.') && remove.mutate(playbook.id, { onSuccess: onClose })}
              >
                <Trash2 /> Delete
              </Button>
            </Can>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Can permission={playbook ? 'crm.playbook.update' : 'crm.playbook.create'}>
              <Button onClick={submit} disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
            </Can>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
